<template>
  <form-field-v2
    :label="label"
    :hint="hint"
    :error="errorMessage"
    :help="help"
    :disabled="disabled"
    :required="required"
    :label-for="controlId"
  >
    <div class="relative" :class="groupClasses()">
      <slot name="prefix" />
      <span
        v-if="icon"
        class="material-symbols-outlined text-[24px] absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 pointer-events-none"
        :class="disabled ? 'text-neutral-400' : 'text-[#6B7280]'"
      >
        {{ icon }}
      </span>
      <textarea
        v-if="multiline"
        :id="controlId"
        ref="control"
        :value="value"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        class="input-v2 w-full block"
        :class="controlClasses"
        v-bind="$attrs"
        v-on="controlListeners"
      />
      <input
        v-else
        :id="controlId"
        ref="control"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        class="input-v2 w-full"
        :class="controlClasses"
        v-bind="$attrs"
        v-on="controlListeners"
      />
      <slot name="suffix" />
    </div>
  </form-field-v2>
</template>

<script>
import FormFieldV2 from './FormFieldV2.vue';

let instanceCount = 0;

export default {
  name: 'InputV2',
  components: { FormFieldV2 },
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    type: { type: String, default: 'text' },
    label: { type: String, default: '' },
    hint: { type: String, default: '' },
    error: { type: [String, Boolean], default: '' },
    help: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    multiline: { type: Boolean, default: false },
    rows: { type: Number, default: 1 },
    autoResize: { type: Boolean, default: false },
    inputClass: { type: [String, Array, Object], default: '' },
  },
  data() {
    instanceCount += 1;
    return { instanceId: instanceCount };
  },
  computed: {
    controlId() {
      return this.$attrs.id || `input-v2-${this.instanceId}`;
    },
    errorMessage() {
      return typeof this.error === 'string' ? this.error : '';
    },
    controlClasses() {
      return [
        {
          'ltr:pl-[43px] rtl:pr-[43px]': !!this.icon,
          'is-error': !!this.error,
          'min-h-[44px]': this.multiline,
          '!h-auto': this.multiline && !this.autoResize,
          'resize-none overflow-hidden': this.multiline && this.autoResize,
        },
        this.inputClass,
      ];
    },
    controlListeners() {
      return {
        ...this.$listeners,
        input: event => this.$emit('input', event.target.value),
      };
    },
  },
  watch: {
    value() {
      this.$nextTick(this.resize);
    },
  },
  mounted() {
    this.resize();
  },
  methods: {
    groupClasses() {
      if (!this.$scopedSlots.prefix && !this.$scopedSlots.suffix) return null;
      return [
        'input-v2-group',
        { 'is-error': !!this.error, 'is-disabled': this.disabled },
      ];
    },
    resize() {
      const el = this.$refs.control;
      if (!this.multiline || !this.autoResize || !el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },
    focus() {
      this.$refs.control?.focus();
    },
  },
};
</script>
